export interface Point {
  x: number
  y: number
  time_offset: number // microseconds from stroke start
  force: number
}

export interface Stroke {
  width: number
  points: Point[]
  color: string
  time_offset?: number  // stroke의 시작 시간 (페이지 기준, microseconds)
}

export interface Page {
  page_index: number
  created_at: string
  strokes: Stroke[]
}

export interface DrawingDataWrapper {
  creator?: string
  format?: number
  drawings: Page[]
}

export type DrawingDataStructure = Page[]

// Helper function to extract pages array from either old or new format
export function extractDrawingPages(data: unknown): Page[] {
  if (!data) return []

  // New format: { creator, format, drawings: [...] }
  if (typeof data === 'object' && 'drawings' in data && Array.isArray((data as DrawingDataWrapper).drawings)) {
    return (data as DrawingDataWrapper).drawings
  }

  // Old format: directly an array
  if (Array.isArray(data)) {
    return data as Page[]
  }

  return []
}

export interface CanvasBounds {
  minX: number
  minY: number
  maxX: number
  maxY: number
  width: number
  height: number
  padding: number
}

export interface StrokeTimingInfo {
  duration: number
  delay: number
  pathD: string
  pathLength: number
}

export function pointsToPath(points: Point[]): string {
  if (points.length === 0) return ''

  const pathCommands: string[] = []

  // Start with the first point
  pathCommands.push(`M ${points[0].x} ${points[0].y}`)

  // Add line segments for the rest of the points
  for (let i = 1; i < points.length; i++) {
    pathCommands.push(`L ${points[i].x} ${points[i].y}`)
  }

  return pathCommands.join(' ')
}

export function calculatePathLength(points: Point[]): number {
  let length = 0
  for (let i = 1; i < points.length; i++) {
    const dx = points[i].x - points[i - 1].x
    const dy = points[i].y - points[i - 1].y
    length += Math.sqrt(dx * dx + dy * dy)
  }
  return length
}

export function getStrokeDuration(stroke: Stroke): number {
  if (stroke.points.length === 0) return 0
  const firstPoint = stroke.points[0]
  const lastPoint = stroke.points[stroke.points.length - 1]
  return (lastPoint.time_offset - firstPoint.time_offset) / 1_000_000
}

/**
 * Calculates the absolute end time of a stroke in seconds
 */
export function getStrokeEndTime(stroke: Stroke): number {
  if (stroke.points.length === 0) return 0

  const strokeStartTime = (stroke.time_offset ?? 0) / 1_000_000
  const lastPointOffset = stroke.points[stroke.points.length - 1].time_offset / 1_000_000

  return strokeStartTime + lastPointOffset
}

export interface TimeRange {
  minTime: number  // Earliest stroke end time in seconds
  maxTime: number  // Latest stroke end time in seconds
  range: number    // Time span (maxTime - minTime)
}

/**
 * Calculates the time range of stroke end times across all pages
 */
export function calculateTimeRange(pages: Page[]): TimeRange {
  let minTime = Infinity
  let maxTime = -Infinity
  let hasValidStrokes = false

  pages.forEach(page => {
    page.strokes.forEach(stroke => {
      if (stroke.points.length === 0) return

      const endTime = getStrokeEndTime(stroke)
      minTime = Math.min(minTime, endTime)
      maxTime = Math.max(maxTime, endTime)
      hasValidStrokes = true
    })
  })

  if (!hasValidStrokes) {
    return { minTime: 0, maxTime: 0, range: 0 }
  }

  return {
    minTime,
    maxTime,
    range: maxTime - minTime
  }
}

// 5 discrete grayscale steps: #CCCCCC → #999999 → #666666 → #333333 → #000000
const GRAYSCALE_STEPS = [
  '#CCCCCC',  // 0-20%: Lightest gray (204, 204, 204)
  '#999999',  // 20-40%: Light-medium gray (153, 153, 153)
  '#666666',  // 40-60%: Medium gray (102, 102, 102)
  '#333333',  // 60-80%: Dark-medium gray (51, 51, 51)
  '#000000',  // 80-100%: Black (0, 0, 0)
] as const

/**
 * Maps a stroke to a grayscale color based on its end time
 */
export function getGrayscaleColor(stroke: Stroke, timeRange: TimeRange): string {
  if (timeRange.range === 0) {
    return GRAYSCALE_STEPS[0]
  }

  const endTime = getStrokeEndTime(stroke)
  const normalizedTime = (endTime - timeRange.minTime) / timeRange.range

  // Map to one of 5 discrete steps
  const stepIndex = Math.min(
    Math.floor(normalizedTime * 5),
    GRAYSCALE_STEPS.length - 1
  )

  return GRAYSCALE_STEPS[stepIndex]
}

export function calculateCumulativeStartTimes(strokes: Stroke[]): number[] {
  const startTimes: number[] = []

  for (const stroke of strokes) {
    // stroke에 time_offset이 있으면 그것을 사용 (절대 시간, microseconds를 seconds로 변환)
    if (stroke.time_offset !== undefined) {
      startTimes.push(stroke.time_offset / 1_000_000)
    } else {
      // 없으면 0 (하위 호환성)
      startTimes.push(0)
    }
  }

  return startTimes
}

export function getTotalDuration(strokes: Stroke[]): number {
  if (strokes.length === 0) return 0

  let maxEndTime = 0

  for (const stroke of strokes) {
    if (stroke.points.length === 0) continue

    // stroke의 시작 시간 (microseconds를 seconds로 변환)
    const strokeStartTime = stroke.time_offset !== undefined ? stroke.time_offset / 1_000_000 : 0

    // stroke 내부의 마지막 point의 time_offset (stroke 시작 기준 상대 시간, microseconds를 seconds로 변환)
    const strokeDuration = stroke.points[stroke.points.length - 1].time_offset / 1_000_000

    // stroke의 실제 종료 시간 (절대 시간, seconds)
    const strokeEndTime = strokeStartTime + strokeDuration

    maxEndTime = Math.max(maxEndTime, strokeEndTime)
  }

  return maxEndTime
}

export function calculateCanvasBounds(pages: Page[], padding = 20): CanvasBounds {
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity

  pages.forEach(page => {
    page.strokes.forEach(stroke => {
      stroke.points.forEach(point => {
        minX = Math.min(minX, point.x)
        minY = Math.min(minY, point.y)
        maxX = Math.max(maxX, point.x)
        maxY = Math.max(maxY, point.y)
      })
    })
  })

  const width = maxX - minX + padding * 2
  const height = maxY - minY + padding * 2

  return { minX, minY, maxX, maxY, width, height, padding }
}

export function getStrokeTimingInfo(
  stroke: Stroke,
  cumulativeStartTime: number
): StrokeTimingInfo {
  const pathD = pointsToPath(stroke.points)
  const pathLength = calculatePathLength(stroke.points)
  const duration = getStrokeDuration(stroke)
  const delay = cumulativeStartTime

  return { duration, delay, pathD, pathLength }
}

export interface Snapshot {
  strokes: Stroke[]
  timeMs: number
  strokeIndex: number
  startTimeMs: number
}

export function extractSnapshots(pages: Page[], minGapMs = 1000): Snapshot[] {
  const snapshots: Snapshot[] = []
  const allStrokes: { stroke: Stroke; pageIndex: number }[] = []

  // 모든 페이지의 stroke들을 시간순으로 정렬
  pages.forEach(page => {
    page.strokes.forEach(stroke => {
      allStrokes.push({ stroke, pageIndex: page.page_index })
    })
  })

  // time_offset으로 정렬
  allStrokes.sort((a, b) => {
    const timeA = a.stroke.time_offset ?? 0
    const timeB = b.stroke.time_offset ?? 0
    return timeA - timeB
  })

  // 시작 시간 찾기 (microseconds를 milliseconds로 변환)
  const startTimeMs = allStrokes.length > 0 ? (allStrokes[0].stroke.time_offset ?? 0) / 1000 : 0

  let currentSnapshot: Stroke[] = []
  let previousEndTime = 0 // seconds 단위로 작업

  allStrokes.forEach(({ stroke }, index) => {
    const strokeStartTime = (stroke.time_offset ?? 0) / 1_000_000 // microseconds를 seconds로 변환
    const strokeDuration = stroke.points.length > 0
      ? stroke.points[stroke.points.length - 1].time_offset / 1_000_000 // microseconds를 seconds로 변환
      : 0
    const strokeEndTime = strokeStartTime + strokeDuration

    // 이전 stroke와의 시간 간격 체크 (seconds를 milliseconds로 변환하여 비교)
    const gapMs = (strokeStartTime - previousEndTime) * 1000

    // minGapMs 이상 차이 나면 스냅샷 저장
    if (gapMs >= minGapMs && currentSnapshot.length > 0) {
      snapshots.push({
        strokes: [...currentSnapshot],
        timeMs: previousEndTime * 1000, // seconds를 milliseconds로 변환
        strokeIndex: index - 1,
        startTimeMs
      })
    }

    currentSnapshot.push(stroke)
    previousEndTime = strokeEndTime
  })

  // 마지막 스냅샷 추가
  if (currentSnapshot.length > 0) {
    snapshots.push({
      strokes: currentSnapshot,
      timeMs: previousEndTime * 1000, // seconds를 milliseconds로 변환
      strokeIndex: allStrokes.length - 1,
      startTimeMs
    })
  }

  return snapshots
}

export function generateSnapshotSVG(
  strokes: Stroke[],
  bounds: CanvasBounds,
  timeMs: number,
  _startTimeMs: number
): string {
  const { minX, minY, width, height, padding } = bounds

  let svgContent = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="${minX - padding} ${minY - padding} ${width} ${height}">
`

  svgContent += `  <rect width="${width}" height="${height}" x="${minX - padding}" y="${minY - padding}" fill="white"/>
`

  // 모든 stroke 렌더링
  strokes.forEach(stroke => {
    if (!stroke.points || stroke.points.length === 0) return

    const pathD = pointsToPath(stroke.points)
    svgContent += `  <path d="${pathD}" stroke="${stroke.color || '#000000'}" stroke-width="${stroke.width || 2}" fill="none" stroke-linecap="round" stroke-linejoin="round"/>\n`
  })

  // 시간 표시 텍스트 추가 (오른쪽 하단) - 절대 시간 표시
  const absoluteTimeSec = timeMs / 1000 // 절대 시간 (초)
  const textX = minX + width - padding - 10
  const textY = minY + height - padding - 10
  const timeText = `${absoluteTimeSec.toFixed(4)}s`

  svgContent += `  <text x="${textX}" y="${textY}" text-anchor="end" font-family="monospace" font-size="20" font-weight="bold" fill="#444">${timeText}</text>\n`

  svgContent += `</svg>`

  return svgContent
}

export function generateSVGContent(
  pages: Page[],
  bounds: CanvasBounds,
  animated: boolean
): string {
  const { minX, minY, width, height, padding } = bounds

  let svgContent = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="${minX - padding} ${minY - padding} ${width} ${height}">
`

  // Add keyframe animation if animated
  if (animated) {
    svgContent += `  <style>
    @keyframes drawStroke {
      to {
        stroke-dashoffset: 0;
      }
    }
  </style>
`
  }

  svgContent += `  <rect width="${width}" height="${height}" x="${minX - padding}" y="${minY - padding}" fill="white"/>
`

  pages.forEach(page => {
    page.strokes.forEach(stroke => {
      if (!stroke.points || stroke.points.length === 0) return

      // stroke의 시작 시간 (stroke.time_offset 사용)
      const strokeStartTime = stroke.time_offset !== undefined ? stroke.time_offset : 0
      const timingInfo = getStrokeTimingInfo(stroke, strokeStartTime)

      if (animated) {
        svgContent += `  <path d="${timingInfo.pathD}" stroke="${stroke.color || '#000000'}" stroke-width="${stroke.width || 2}" fill="none" stroke-linecap="round" stroke-linejoin="round" stroke-dasharray="${timingInfo.pathLength}" stroke-dashoffset="${timingInfo.pathLength}" style="animation: drawStroke ${timingInfo.duration}s linear ${timingInfo.delay}s forwards;"/>\n`
      } else {
        svgContent += `  <path d="${timingInfo.pathD}" stroke="${stroke.color || '#000000'}" stroke-width="${stroke.width || 2}" fill="none" stroke-linecap="round" stroke-linejoin="round"/>\n`
      }
    })
  })

  svgContent += `</svg>`

  return svgContent
}

export function generateGrayscaleSVGContent(
  pages: Page[],
  bounds: CanvasBounds
): string {
  const { minX, minY, width, height, padding } = bounds

  // Add extra space for legend
  const legendExtraSpace = 70
  const totalHeight = height + legendExtraSpace

  // Calculate time range
  const timeRange = calculateTimeRange(pages)

  let svgContent = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${totalHeight}" viewBox="${minX - padding} ${minY - padding} ${width} ${totalHeight}">
`

  svgContent += `  <rect width="${width}" height="${totalHeight}" x="${minX - padding}" y="${minY - padding}" fill="white"/>
`

  // Render all strokes with grayscale colors
  pages.forEach(page => {
    page.strokes.forEach(stroke => {
      if (!stroke.points || stroke.points.length === 0) return

      const pathD = pointsToPath(stroke.points)
      const color = getGrayscaleColor(stroke, timeRange)

      svgContent += `  <path d="${pathD}" stroke="${color}" stroke-width="${stroke.width || 2}" fill="none" stroke-linecap="round" stroke-linejoin="round"/>\n`
    })
  })

  // Add legend
  const legendHeight = 40
  const legendWidth = 420
  const legendMargin = 15
  const legendX = minX - padding + (width - legendWidth) / 2
  const legendY = minY - padding + totalHeight - legendHeight - legendMargin

  // Legend background
  svgContent += `  <rect x="${legendX}" y="${legendY}" width="${legendWidth}" height="${legendHeight}" fill="white" stroke="#E5E7EB" stroke-width="1" rx="4" opacity="0.95"/>\n`

  // Legend title
  svgContent += `  <text x="${legendX + 10}" y="${legendY + 22}" font-size="12" fill="#4B5563" font-weight="600" font-family="system-ui, -apple-system, sans-serif">시간 범위:</text>\n`

  // Color swatches and labels
  const colors = ['#CCCCCC', '#999999', '#666666', '#333333', '#000000']
  colors.forEach((color, idx) => {
    const endPercent = (idx + 1) * 20
    const endTime = timeRange.minTime + (timeRange.range * endPercent / 100)
    const endTimeRounded = Math.ceil(endTime * 10) / 10
    const xOffset = legendX + 75 + (idx * 67)

    // Color swatch
    svgContent += `  <rect x="${xOffset}" y="${legendY + 10}" width="16" height="16" fill="${color}" stroke="#D1D5DB" stroke-width="1" rx="2"/>\n`

    // Time label
    svgContent += `  <text x="${xOffset + 20}" y="${legendY + 22}" font-size="10" fill="#374151" font-family="monospace">~${endTimeRounded.toFixed(1)}s</text>\n`
  })

  svgContent += `</svg>`

  return svgContent
}
